import { useEffect, useState } from 'react';
import type {
  GenerateRecoveryCodesResponse,
  PasskeyCredentialSummary,
  ResolvedAuthPolicyResponse,
} from '@ghalishor/contracts';
import type { IdentityApiClient } from './api-client';
import { identityErrorMessage } from './api-client';
import { passkeyUserMessage, passkeysSupported, registerPasskey } from './passkey';

export function AccountSecurityPanel(props: {
  readonly api: IdentityApiClient;
  readonly busy: boolean;
  readonly onBusy: (busy: boolean) => void;
  readonly onInfo: (message: string | null) => void;
  readonly onError: (message: string | null) => void;
}) {
  const [policy, setPolicy] = useState<ResolvedAuthPolicyResponse | null>(null);
  const [passkeys, setPasskeys] = useState<readonly PasskeyCredentialSummary[]>([]);
  const [freshCodes, setFreshCodes] = useState<GenerateRecoveryCodesResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [nextPolicy, listed] = await Promise.all([
        props.api.getAuthPolicy(),
        props.api.listPasskeys(),
      ]);
      if (!nextPolicy.enabled) {
        setPolicy(null);
        setPasskeys([]);
        return;
      }
      setPolicy(nextPolicy);
      setPasskeys(listed.items);
    } catch {
      // Passkey/policy backend may be unavailable; keep the account page usable.
      setPolicy(null);
      setPasskeys([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per mount / api identity
  }, [props.api]);

  if (loading) {
    return null;
  }

  if (!policy || policy.enabled !== true) {
    return null;
  }

  return (
    <section className="account-security">
      <h3>امنیت ورود</h3>
      <p className="account-security__hint">
        سیاست «{policy.policy.nameFa}» — Passkey فعال: {policy.passkeyCount}
        {policy.policy.minimumPasskeys > 0
          ? ` (حداقل لازم: ${policy.policy.minimumPasskeys})`
          : ''}
        {policy.enrollmentRequired
          ? ` — هنوز ${policy.passkeysNeeded} Passkey دیگر برای کنسول مدیریت لازم است.`
          : ''}
      </p>
      {policy.suggestPasskeyEnrollment ? (
        <p className="account-security__suggest">
          پیشنهاد: حداقل یک Passkey روی این دستگاه ثبت کنید تا بدون کد پیامکی وارد شوید.
        </p>
      ) : null}

      {passkeysSupported() ? (
        <button
          type="button"
          className="secondary"
          disabled={props.busy}
          onClick={() => {
            props.onBusy(true);
            props.onError(null);
            void (async () => {
              try {
                const name = await registerPasskey(props.api, 'این دستگاه');
                props.onInfo(`Passkey «${name}» ثبت شد.`);
                await reload();
              } catch (err: unknown) {
                props.onError(passkeyUserMessage(err));
              } finally {
                props.onBusy(false);
              }
            })();
          }}
        >
          ثبت Passkey جدید
        </button>
      ) : null}

      {passkeys.length > 0 ? (
        <ul className="account-security__list">
          {passkeys.map((item) => (
            <li key={item.id}>
              <span>
                {item.deviceName} — {item.status === 'active' ? 'فعال' : 'غیرفعال'}
              </span>
              {item.status === 'active' ? (
                <button
                  type="button"
                  className="secondary"
                  disabled={props.busy}
                  onClick={() => {
                    props.onBusy(true);
                    props.onError(null);
                    void (async () => {
                      try {
                        await props.api.revokePasskey(item.id);
                        props.onInfo(`Passkey «${item.deviceName}» غیرفعال شد.`);
                        await reload();
                      } catch (err: unknown) {
                        props.onError(identityErrorMessage(err));
                      } finally {
                        props.onBusy(false);
                      }
                    })();
                  }}
                >
                  حذف
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="account-security__hint">هنوز Passkey ثبت نشده است.</p>
      )}

      <h4>کدهای بازیابی اضطراری</h4>
      <p className="account-security__hint">
        باقی‌مانده: {policy.recoveryCodesRemaining}. هر کد یک‌بارمصرف است؛ پس از تولید، نسخهٔ قبلی باطل می‌شود.
      </p>
      <button
        type="button"
        className="secondary"
        disabled={props.busy || !policy.policy.recoveryCodeAllowed}
        onClick={() => {
          props.onBusy(true);
          props.onError(null);
          void (async () => {
            try {
              const generated = await props.api.generateRecoveryCodes();
              setFreshCodes(generated);
              props.onInfo('کدهای بازیابی ساخته شد. همین حالا ذخیره کنید؛ دوباره نمایش داده نمی‌شوند.');
              await reload();
            } catch (err: unknown) {
              props.onError(identityErrorMessage(err));
            } finally {
              props.onBusy(false);
            }
          })();
        }}
      >
        تولید ۱۰ کد بازیابی جدید
      </button>
      {freshCodes ? (
        <pre className="account-security__codes">{freshCodes.codes.join('\n')}</pre>
      ) : null}
    </section>
  );
}
